import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import classnames from 'classnames';
import { useAtomValue } from 'jotai';
import { IImage } from '@sapientpro/sapientpro-data-models';
import { useState } from 'react';
import Img from '../../../components/Img';
import { theme } from '../../../store/theme';
import styles from './Article.module.scss';

type ImagesSliderProps = {
  images?: IImage[],
  items?: { title: string, description: string, link: string, image: string, darkImage?: string }[]
};
const ImagesSlider = ({ images, items }: ImagesSliderProps) => {
  const appTheme = useAtomValue(theme);

  const settings = {
    omitEnd: true,
    perMove: 1,
    perPage: 1,
    pagination: true,
    gap: 0,
    classes: {
      pagination: `splide__pagination ${styles.pagination}`,
      page: `splide__pagination__page ${styles.pagination__item}`,
    },
  };

  const defaultUrl = `/media/slider-arrow-right-${appTheme}.svg`;
  const hoveredUrl = `/media/slider-arrow-right-hovered-${appTheme}.svg`;
  const pressedUrl = `/media/slider-arrow-right-pressed-${appTheme}.svg`;

  const [leftArrowSrc, setLeftArrowSrc] = useState<string>(defaultUrl);
  const [rightArrowSrc, setRightArrowSrc] = useState<string>(defaultUrl);

  const handleMouseEnter = (setArrowSrc: (src: string) => void, newSrc: string, isDisabled: boolean) => {
    if (!isDisabled) setArrowSrc(newSrc);
  };

  return (
    <div
      className={styles.articleCarousel}
    >
      <Splide
        hasTrack={false}
        options={settings}
      >
        <SplideTrack className={styles.track}>
          {images ? images.map(image => (
            <SplideSlide
              key={image.id}
              className={styles.articleCarouselImage}
            >
              <Img
                src={image.url}
                alt='article image'
                width={image.width}
                height={image.height}
                withPreloader
              />
            </SplideSlide>
          )) : items?.map(image => {
            const imageBasedOnUserTheme = (appTheme === 'dark' && !!image?.darkImage) ? image.darkImage : image.image;
            return (
              <SplideSlide
                key={image.title}
                className={styles.articleCarouselImage}
              >
                <Img
                  src={imageBasedOnUserTheme}
                  alt={image.title}
                  withPreloader
                />
                {!!image.description && (<p className={styles.articleCarouselImage__description}>{image.description}</p>)}
              </SplideSlide>
            );
          })}
        </SplideTrack>
        <div className={classnames(styles.navigation, 'splide__arrows')}>
          <button
            type='button'
            className={classnames('splide__arrow splide__arrow--prev', styles.arrow, styles.arrow_prev)}
            onMouseEnter={(e) => handleMouseEnter(setLeftArrowSrc, hoveredUrl, e.currentTarget.disabled)}
            onMouseLeave={(e) => handleMouseEnter(setLeftArrowSrc, defaultUrl, e.currentTarget.disabled)}
            onMouseDown={(e) => handleMouseEnter(setLeftArrowSrc, pressedUrl, e.currentTarget.disabled)}
          >
            <Img
              src={leftArrowSrc}
              alt='slider arrow left'
            />
          </button>
          <div className={classnames('splide__pagination', styles.pagination)} />
          <button
            type='button'
            className={classnames('splide__arrow splide__arrow--next', styles.arrow, styles.arrow_next)}
            onMouseEnter={(e) => handleMouseEnter(setRightArrowSrc, hoveredUrl, e.currentTarget.disabled)}
            onMouseLeave={(e) => handleMouseEnter(setRightArrowSrc, defaultUrl, e.currentTarget.disabled)}
            onMouseDown={(e) => handleMouseEnter(setRightArrowSrc, pressedUrl, e.currentTarget.disabled)}
          >
            <Img
              src={rightArrowSrc}
              alt='slider arrow right'
            />
          </button>
        </div>
      </Splide>
    </div>
  );
};

export default ImagesSlider;
